import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  X, Star, Lock, Check, Crown, Zap, Gift, 
  ChevronLeft, ChevronRight, Sparkles, Trophy,
  CreditCard, Loader2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Tier rewards configuration (synced with backend)
const TIER_REWARDS = {
  1: {free: {type: "stars", amount: 5, emoji: "⭐"}, premium: {type: "skin", id: "golden_banana", name: "Golden Banana", emoji: "🍌"}},
  2: {free: {type: "bomb", amount: 1, emoji: "💣"}, premium: {type: "stars", amount: 15, emoji: "⭐"}},
  3: {free: {type: "stars", amount: 10, emoji: "⭐"}, premium: {type: "effect", id: "sparkle_trail", name: "Sparkle Trail", emoji: "✨"}},
  4: {free: {type: "extra_moves", amount: 1, emoji: "➕"}, premium: {type: "bomb", amount: 2, emoji: "💣"}},
  5: {free: {type: "stars", amount: 15, emoji: "⭐"}, premium: {type: "skin", id: "crystal_apple", name: "Crystal Apple", emoji: "🍎"}},
  6: {free: {type: "bomb", amount: 1, emoji: "💣"}, premium: {type: "stars", amount: 20, emoji: "⭐"}},
  7: {free: {type: "stars", amount: 10, emoji: "⭐"}, premium: {type: "extra_moves", amount: 2, emoji: "➕"}},
  8: {free: {type: "double_xp", amount: 1, emoji: "2️⃣"}, premium: {type: "theme", id: "galaxy", name: "Galaxy Board", emoji: "🌌"}},
  9: {free: {type: "stars", amount: 15, emoji: "⭐"}, premium: {type: "color_bomb", amount: 2, emoji: "🎨"}},
  10: {free: {type: "color_bomb", amount: 1, emoji: "🎨"}, premium: {type: "skin", id: "neon_orange", name: "Neon Orange", emoji: "🍊"}},
  11: {free: {type: "stars", amount: 10, emoji: "⭐"}, premium: {type: "stars", amount: 25, emoji: "⭐"}},
  12: {free: {type: "bomb", amount: 1, emoji: "💣"}, premium: {type: "effect", id: "fire_match", name: "Fire Match", emoji: "🔥"}},
  13: {free: {type: "stars", amount: 15, emoji: "⭐"}, premium: {type: "wrapped_fruit", amount: 2, emoji: "🎁"}},
  14: {free: {type: "extra_moves", amount: 1, emoji: "➕"}, premium: {type: "bomb", amount: 3, emoji: "💣"}},
  15: {free: {type: "stars", amount: 20, emoji: "⭐"}, premium: {type: "skin", id: "diamond_grape", name: "Diamond Grape", emoji: "🍇"}},
  16: {free: {type: "bomb", amount: 1, emoji: "💣"}, premium: {type: "stars", amount: 30, emoji: "⭐"}},
  17: {free: {type: "stars", amount: 10, emoji: "⭐"}, premium: {type: "double_xp", amount: 2, emoji: "2️⃣"}},
  18: {free: {type: "double_xp", amount: 1, emoji: "2️⃣"}, premium: {type: "theme", id: "underwater", name: "Underwater Board", emoji: "🌊"}},
  19: {free: {type: "stars", amount: 15, emoji: "⭐"}, premium: {type: "extra_moves", amount: 3, emoji: "➕"}},
  20: {free: {type: "wrapped_fruit", amount: 1, emoji: "🎁"}, premium: {type: "skin", id: "ruby_cherry", name: "Ruby Cherry", emoji: "🍒"}},
  21: {free: {type: "stars", amount: 15, emoji: "⭐"}, premium: {type: "stars", amount: 35, emoji: "⭐"}},
  22: {free: {type: "bomb", amount: 2, emoji: "💣"}, premium: {type: "color_bomb", amount: 3, emoji: "🎨"}},
  23: {free: {type: "stars", amount: 20, emoji: "⭐"}, premium: {type: "effect", id: "rainbow_burst", name: "Rainbow Burst", emoji: "🌈"}},
  24: {free: {type: "extra_moves", amount: 1, emoji: "➕"}, premium: {type: "wrapped_fruit", amount: 3, emoji: "🎁"}},
  25: {free: {type: "stars", amount: 25, emoji: "⭐"}, premium: {type: "frame", id: "gold_frame", name: "Gold Frame", emoji: "🖼️"}},
  26: {free: {type: "color_bomb", amount: 1, emoji: "🎨"}, premium: {type: "stars", amount: 40, emoji: "⭐"}},
  27: {free: {type: "stars", amount: 15, emoji: "⭐"}, premium: {type: "bomb", amount: 4, emoji: "💣"}},
  28: {free: {type: "bomb", amount: 2, emoji: "💣"}, premium: {type: "theme", id: "candy_land", name: "Candy Land Board", emoji: "🍬"}},
  29: {free: {type: "stars", amount: 20, emoji: "⭐"}, premium: {type: "disco_fruit", amount: 2, emoji: "🪩"}},
  30: {free: {type: "disco_fruit", amount: 1, emoji: "🪩"}, premium: {type: "skin", id: "plasma_pear", name: "Plasma Pear", emoji: "🍐"}},
  31: {free: {type: "stars", amount: 20, emoji: "⭐"}, premium: {type: "stars", amount: 45, emoji: "⭐"}},
  32: {free: {type: "extra_moves", amount: 2, emoji: "➕"}, premium: {type: "extra_moves", amount: 4, emoji: "➕"}},
  33: {free: {type: "stars", amount: 25, emoji: "⭐"}, premium: {type: "effect", id: "lightning_strike", name: "Lightning Strike", emoji: "⚡"}},
  34: {free: {type: "bomb", amount: 2, emoji: "💣"}, premium: {type: "color_bomb", amount: 4, emoji: "🎨"}},
  35: {free: {type: "stars", amount: 30, emoji: "⭐"}, premium: {type: "skin", id: "cosmic_melon", name: "Cosmic Melon", emoji: "🍈"}},
  36: {free: {type: "double_xp", amount: 1, emoji: "2️⃣"}, premium: {type: "stars", amount: 50, emoji: "⭐"}},
  37: {free: {type: "stars", amount: 20, emoji: "⭐"}, premium: {type: "wrapped_fruit", amount: 4, emoji: "🎁"}},
  38: {free: {type: "wrapped_fruit", amount: 1, emoji: "🎁"}, premium: {type: "theme", id: "neon_city", name: "Neon City Board", emoji: "🌃"}},
  39: {free: {type: "stars", amount: 25, emoji: "⭐"}, premium: {type: "disco_fruit", amount: 3, emoji: "🪩"}},
  40: {free: {type: "color_bomb", amount: 2, emoji: "🎨"}, premium: {type: "frame", id: "diamond_frame", name: "Diamond Frame", emoji: "💎"}},
  41: {free: {type: "stars", amount: 25, emoji: "⭐"}, premium: {type: "stars", amount: 60, emoji: "⭐"}},
  42: {free: {type: "bomb", amount: 3, emoji: "💣"}, premium: {type: "bomb", amount: 5, emoji: "💣"}},
  43: {free: {type: "stars", amount: 30, emoji: "⭐"}, premium: {type: "effect", id: "galaxy_swirl", name: "Galaxy Swirl", emoji: "🌀"}},
  44: {free: {type: "extra_moves", amount: 2, emoji: "➕"}, premium: {type: "extra_moves", amount: 5, emoji: "➕"}},
  45: {free: {type: "stars", amount: 35, emoji: "⭐"}, premium: {type: "skin", id: "inferno_strawberry", name: "Inferno Strawberry", emoji: "🍓"}},
  46: {free: {type: "disco_fruit", amount: 1, emoji: "🪩"}, premium: {type: "stars", amount: 75, emoji: "⭐"}},
  47: {free: {type: "stars", amount: 30, emoji: "⭐"}, premium: {type: "color_bomb", amount: 5, emoji: "🎨"}},
  48: {free: {type: "double_xp", amount: 2, emoji: "2️⃣"}, premium: {type: "theme", id: "royal_palace", name: "Royal Palace Board", emoji: "🏰"}},
  49: {free: {type: "stars", amount: 40, emoji: "⭐"}, premium: {type: "disco_fruit", amount: 5, emoji: "🪩"}},
  50: {free: {type: "stars", amount: 50, emoji: "⭐"}, premium: {type: "legendary", id: "fruit_master_title", name: "🏆 Fruit Master Title", emoji: "👑"}},
};

const XP_PER_TIER = 500;
const TOTAL_TIERS = 50;

export function BattlePassModal({ 
  isOpen, 
  onClose, 
  battlePass,
  onUpdateBattlePass,
  onClaimReward,
  onPurchasePremium
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const [purchasing, setPurchasing] = useState(false);
  const [claiming, setClaiming] = useState(null);
  
  const tiersPerPage = 5;
  const totalPages = Math.ceil(TOTAL_TIERS / tiersPerPage);
  
  // Initialize battle pass if not exists
  const bp = battlePass || {
    is_premium: false,
    current_xp: 0,
    current_tier: 0,
    claimed_free: [],
    claimed_premium: [],
    season_number: 1
  };
  
  // Use backend's current_tier if available, otherwise calculate from XP
  const currentTier = bp.current_tier !== undefined 
    ? bp.current_tier 
    : Math.min(Math.floor(bp.current_xp / XP_PER_TIER), TOTAL_TIERS);
  const xpInCurrentTier = bp.current_xp % XP_PER_TIER;
  const progressPercent = (xpInCurrentTier / XP_PER_TIER) * 100;

  // Jump to current tier page on open
  useEffect(() => {
    if (isOpen) {
      const tierPage = Math.floor(currentTier / tiersPerPage);
      setCurrentPage(Math.min(tierPage, totalPages - 1));
    }
  }, [isOpen, currentTier]);

  const handlePurchasePremium = async () => {
    setPurchasing(true);
    try {
      // Get the user's ID for the subscription
      const token = localStorage.getItem('fruitCrush_token');
      let userId = null;
      
      if (token) {
        try {
          const userResponse = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            userId = userData.id;
          }
        } catch (e) {
          console.log('Could not get user ID for subscription');
        }
      }
      
      // Use the subscription endpoint instead of one-time checkout
      const response = await fetch(`${API_URL}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin + window.location.pathname,
          user_id: userId,
        }),
      });
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Subscription failed:', error);
    } finally {
      setPurchasing(false);
    }
  };

  const handleClaimReward = async (tier, track) => {
    setClaiming(`${tier}-${track}`);
    try {
      if (onClaimReward) {
        await onClaimReward(tier, track);
      }
    } finally {
      setClaiming(null);
    }
  };

  const canClaimFree = (tier) => {
    return currentTier >= tier && !bp.claimed_free?.includes(tier);
  };

  const canClaimPremium = (tier) => {
    return bp.is_premium && currentTier >= tier && !bp.claimed_premium?.includes(tier);
  };

  const getRewardDisplay = (reward) => {
    if (reward.amount) {
      return `${reward.emoji} x${reward.amount}`;
    }
    return `${reward.emoji} ${reward.name || ''}`;
  };

  if (!isOpen) return null;

  const visibleTiers = Array.from(
    { length: tiersPerPage }, 
    (_, i) => currentPage * tiersPerPage + i + 1
  ).filter(t => t <= TOTAL_TIERS);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card 
        className="w-full max-w-4xl bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 border-purple-500/50 shadow-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 bg-black/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                <Trophy className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  Battle Pass
                  {bp.is_premium && (
                    <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black">
                      <Crown className="w-3 h-3 mr-1" /> PREMIUM
                    </Badge>
                  )}
                </h2>
                <p className="text-purple-200 text-sm">Season {bp.season_number || 1}</p>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-white hover:bg-white/10">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* XP Progress */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-purple-200 mb-1">
              <span>Tier {currentTier} / {TOTAL_TIERS}</span>
              <span className="flex items-center gap-1">
                <Zap className="w-4 h-4 text-yellow-400" />
                {bp.current_xp} XP Total
              </span>
            </div>
            <div className="relative h-4 bg-black/30 rounded-full overflow-hidden">
              <div 
                className="absolute h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
                style={{ width: `${(currentTier / TOTAL_TIERS) * 100}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                {xpInCurrentTier} / {XP_PER_TIER} XP to next tier
              </div>
            </div>
          </div>
          
          {/* Premium Bonus Info */}
          {bp.is_premium ? (
            <div className="mt-3 p-2 bg-green-500/20 rounded-lg border border-green-500/30">
              <p className="text-green-300 text-xs flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                +10% Bonus XP active on all actions!
              </p>
            </div>
          ) : (
            <div className="mt-3 p-2 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
              <p className="text-yellow-300 text-xs flex items-center gap-2">
                <Crown className="w-4 h-4" />
                Upgrade to Premium for exclusive rewards and +10% Bonus XP!
              </p>
            </div>
          )}
        </div>

        {/* Tier Navigation */}
        <div className="flex items-center justify-between px-4 py-2 bg-black/20">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="text-white hover:bg-white/10"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <span className="text-white font-semibold">
            Tiers {currentPage * tiersPerPage + 1} - {Math.min((currentPage + 1) * tiersPerPage, TOTAL_TIERS)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
            disabled={currentPage >= totalPages - 1}
            className="text-white hover:bg-white/10"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Tiers Grid */}
        <div className="p-4 overflow-y-auto max-h-[45vh]">
          <div className="space-y-3">
            {visibleTiers.map((tier) => {
              const rewards = TIER_REWARDS[tier];
              // Tier is unlocked if currentTier >= tier (meaning we've passed that tier)
              const isUnlocked = currentTier >= tier;
              // Tier is current if we're working on it (currentTier + 1 == tier)
              const isCurrent = currentTier + 1 === tier;
              
              return (
                <div 
                  key={tier}
                  className={`flex items-stretch gap-3 p-3 rounded-xl transition-all ${
                    isCurrent 
                      ? 'bg-purple-500/30 ring-2 ring-purple-400' 
                      : isUnlocked 
                      ? 'bg-white/10' 
                      : 'bg-black/20 opacity-60'
                  }`}
                >
                  {/* Tier Number */}
                  <div className={`w-14 flex-shrink-0 rounded-lg flex flex-col items-center justify-center ${
                    tier === 50 
                      ? 'bg-gradient-to-br from-yellow-400 to-orange-500' 
                      : isUnlocked 
                      ? 'bg-gradient-to-br from-green-400 to-emerald-500' 
                      : isCurrent
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                      : 'bg-gray-700'
                  }`}>
                    {isUnlocked ? (
                      <Check className="w-5 h-5 text-white" />
                    ) : isCurrent ? (
                      <Zap className="w-4 h-4 text-yellow-300" />
                    ) : (
                      <Lock className="w-4 h-4 text-white/60" />
                    )}
                    <span className="text-white font-bold text-sm">{tier}</span>
                  </div>

                  {/* Free Track */}
                  <div className={`flex-1 p-3 rounded-lg ${
                    canClaimFree(tier) 
                      ? 'bg-green-500/20 border border-green-500/50' 
                      : bp.claimed_free?.includes(tier)
                      ? 'bg-gray-500/20 border border-gray-500/30'
                      : 'bg-white/5 border border-white/10'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge variant="outline" className="text-xs mb-1 border-white/30 text-white/70">FREE</Badge>
                        <p className="text-white font-semibold text-sm">{getRewardDisplay(rewards.free)}</p>
                      </div>
                      {bp.claimed_free?.includes(tier) ? (
                        <Badge className="bg-gray-500"><Check className="w-3 h-3" /></Badge>
                      ) : canClaimFree(tier) ? (
                        <Button
                          size="sm"
                          onClick={() => handleClaimReward(tier, 'free')}
                          disabled={claiming === `${tier}-free`}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          {claiming === `${tier}-free` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {/* Premium Track */}
                  <div className={`flex-1 p-3 rounded-lg ${
                    !bp.is_premium
                      ? 'bg-yellow-500/10 border border-yellow-500/30'
                      : canClaimPremium(tier) 
                      ? 'bg-yellow-500/20 border border-yellow-500/50' 
                      : bp.claimed_premium?.includes(tier)
                      ? 'bg-gray-500/20 border border-gray-500/30'
                      : 'bg-white/5 border border-white/10'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge className="text-xs mb-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-black">
                          <Crown className="w-3 h-3 mr-1" /> PREMIUM
                        </Badge>
                        <p className="text-white font-semibold text-sm">{getRewardDisplay(rewards.premium)}</p>
                      </div>
                      {!bp.is_premium ? (
                        <Lock className="w-5 h-5 text-yellow-500/50" />
                      ) : bp.claimed_premium?.includes(tier) ? (
                        <Badge className="bg-gray-500"><Check className="w-3 h-3" /></Badge>
                      ) : canClaimPremium(tier) ? (
                        <Button
                          size="sm"
                          onClick={() => handleClaimReward(tier, 'premium')}
                          disabled={claiming === `${tier}-premium`}
                          className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:opacity-90"
                        >
                          {claiming === `${tier}-premium` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer - Purchase Premium */}
        {!bp.is_premium && (
          <div className="p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-t border-yellow-500/30">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-400" />
                  Unlock Premium Battle Pass
                </h3>
                <p className="text-yellow-200 text-sm">
                  Monthly subscription: exclusive skins, themes, effects + 10% Bonus XP!
                </p>
              </div>
              <Button
                onClick={handlePurchasePremium}
                disabled={purchasing}
                className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold hover:opacity-90 px-6"
              >
                {purchasing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    €7,49/mo
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* XP Sources Info */}
        <div className="p-4 bg-black/20 border-t border-white/10">
          <p className="text-purple-200 text-xs text-center">
            Earn XP: 10% of your Game Score per level completed
            {bp.is_premium && <span className="text-green-400 ml-2">• +10% Premium Bonus</span>}
          </p>
        </div>
      </Card>
    </div>
  );
}

// Battle Pass button for sidebar/header
export function BattlePassButton({ onClick, battlePass }) {
  const bp = battlePass || { current_xp: 0, is_premium: false };
  const currentTier = Math.min(Math.floor(bp.current_xp / XP_PER_TIER), TOTAL_TIERS);
  
  return (
    <Button
      onClick={onClick}
      className={`flex items-center gap-2 ${
        bp.is_premium 
          ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black' 
          : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
      }`}
      data-testid="battle-pass-btn"
    >
      <Trophy className="w-4 h-4" />
      <span className="font-semibold">Tier {currentTier}</span>
      {bp.is_premium && <Crown className="w-4 h-4" />}
    </Button>
  );
}
